import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@repo/shared";

// Local translations
export const T = {
  en_GB: {
    // Card
    passwordAndAuth: "Password & Authentication",
    passwordAndAuthDescription: "Manage your password and add extra security to your account.",
    password: "Password",
    changePassword: "Change Password",
    changePasswordDescription: "Change your account password",
    changePasswordDialogDescription: "Enter your current password and choose a new one",
    currentPassword: "Current Password",
    currentPasswordPlaceholder: "Enter current password",
    newPassword: "New Password",
    newPasswordPlaceholder: "Enter new password",
    newPasswordHint: `Minimum ${PASSWORD_MIN_LENGTH} characters`,
    confirmNewPassword: "Confirm New Password",
    confirmPasswordPlaceholder: "Confirm new password",
    passwordChanged: "Password changed",
    fillAllFields: "Please fill in all fields",
    passwordMinLength: `New password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    passwordMaxLength: `New password must not exceed ${PASSWORD_MAX_LENGTH} characters`,
    passwordsDoNotMatch: "New passwords do not match",
    failedToChangePassword: "Failed to change password",
    somethingWentWrong: "Something went wrong. Please check your connection and try again.",
    authenticatorApp: "Authenticator App (TOTP)",
    authenticatorDescription: "Use an app like Google Authenticator or Authy to generate codes",
    enabled: "Enabled",
    disable: "Disable",
    enable: "Enable",
    // Enable dialog
    enableTitle: "Enable Two-Factor Authentication",
    scanQrCode: "Scan QR Code",
    twoFaEnabled: "2FA Enabled!",
    enterPasswordToBegin: "Enter your password to begin setup",
    scanQrCodeDescription:
      "Scan this QR code with your authenticator app, then enter the code below",
    accountProtected: "Your account is now protected with 2FA",
    twoFactorEnabledSuccess: "Two-factor authentication enabled successfully!",
    twoFactorDisabledSuccess: "Two-factor authentication disabled",
    // Form
    enterPassword: "Enter your password",
    pleaseEnterPassword: "Please enter your password",
    failedToEnable: "Failed to enable 2FA",
    cancel: "Cancel",
    processing: "Processing...",
    continue: "Continue",
    // QR code step
    manualEntryCode: "Manual Entry Code",
    copy: "Copy",
    cantScanQr: "Use this code if you can't scan the QR code",
    verificationCode: "Verification Code",
    enterSixDigitCode: "Enter 6-digit code",
    pleaseEnterSixDigit: "Please enter a 6-digit code",
    invalidCode: "Invalid code",
    verifying: "Verifying...",
    verifyAndEnable: "Verify & Enable",
    // Backup codes
    backupCodes: "Backup Codes",
    backupCodesDescription:
      "Save these codes securely. You can use them to access your account if you lose your authenticator device.",
    copyCodes: "Copy Codes",
    done: "Done",
    // Disable dialog
    disableTitle: "Disable Two-Factor Authentication",
    disableDescription:
      "Enter your password to disable 2FA. This will make your account less secure.",
    failedToDisable: "Failed to disable 2FA",
    disable2Fa: "Disable 2FA",
  },
  nb_NO: {
    // Card
    passwordAndAuth: "Passord og autentisering",
    passwordAndAuthDescription:
      "Administrer passordet ditt og legg til ekstra sikkerhet på kontoen din.",
    password: "Passord",
    changePassword: "Endre passord",
    changePasswordDescription: "Endre kontopassordet ditt",
    changePasswordDialogDescription: "Skriv inn ditt nåværende passord og velg et nytt",
    currentPassword: "Nåværende passord",
    currentPasswordPlaceholder: "Skriv inn nåværende passord",
    newPassword: "Nytt passord",
    newPasswordPlaceholder: "Skriv inn nytt passord",
    newPasswordHint: `Minimum ${PASSWORD_MIN_LENGTH} tegn`,
    confirmNewPassword: "Bekreft nytt passord",
    confirmPasswordPlaceholder: "Bekreft nytt passord",
    passwordChanged: "Passordet er endret",
    fillAllFields: "Vennligst fyll ut alle felt",
    passwordMinLength: `Nytt passord må være minst ${PASSWORD_MIN_LENGTH} tegn`,
    passwordMaxLength: `Nytt passord kan ikke overstige ${PASSWORD_MAX_LENGTH} tegn`,
    passwordsDoNotMatch: "Nye passord stemmer ikke overens",
    failedToChangePassword: "Kunne ikke endre passord",
    somethingWentWrong: "Noe gikk galt. Sjekk tilkoblingen din og prøv igjen.",
    authenticatorApp: "Autentiseringsapp (TOTP)",
    authenticatorDescription:
      "Bruk en app som Google Authenticator eller Authy for å generere koder",
    enabled: "Aktivert",
    disable: "Deaktiver",
    enable: "Aktiver",
    // Enable dialog
    enableTitle: "Aktiver tofaktorautentisering",
    scanQrCode: "Skann QR-kode",
    twoFaEnabled: "2FA aktivert!",
    enterPasswordToBegin: "Skriv inn passordet ditt for å starte oppsettet",
    scanQrCodeDescription:
      "Skann denne QR-koden med autentiseringsappen din, og skriv deretter inn koden nedenfor",
    accountProtected: "Kontoen din er nå beskyttet med 2FA",
    twoFactorEnabledSuccess: "Tofaktorautentisering ble aktivert!",
    twoFactorDisabledSuccess: "Tofaktorautentisering ble deaktivert",
    // Form
    enterPassword: "Skriv inn passordet ditt",
    pleaseEnterPassword: "Vennligst skriv inn passordet ditt",
    failedToEnable: "Kunne ikke aktivere 2FA",
    cancel: "Avbryt",
    processing: "Behandler...",
    continue: "Fortsett",
    // QR code step
    manualEntryCode: "Manuell inntastingskode",
    copy: "Kopier",
    cantScanQr: "Bruk denne koden hvis du ikke kan skanne QR-koden",
    verificationCode: "Bekreftelseskode",
    enterSixDigitCode: "Skriv inn 6-sifret kode",
    pleaseEnterSixDigit: "Vennligst skriv inn en 6-sifret kode",
    invalidCode: "Ugyldig kode",
    verifying: "Verifiserer...",
    verifyAndEnable: "Bekreft og aktiver",
    // Backup codes
    backupCodes: "Sikkerhetskoder",
    backupCodesDescription:
      "Lagre disse kodene sikkert. Du kan bruke dem for å få tilgang til kontoen din hvis du mister autentiseringsenheten.",
    copyCodes: "Kopier koder",
    done: "Ferdig",
    // Disable dialog
    disableTitle: "Deaktiver tofaktorautentisering",
    disableDescription:
      "Skriv inn passordet ditt for å deaktivere 2FA. Dette vil gjøre kontoen din mindre sikker.",
    failedToDisable: "Kunne ikke deaktivere 2FA",
    disable2Fa: "Deaktiver 2FA",
  },
} as const;
